import * as ExpoContacts from 'expo-contacts';
import { PermissionStatus } from 'expo-modules-core';

import { logError, logEvent, logWarning } from '@helpers/Loggers';
import { formatPhoneNumberToE164, parsePhoneNumber } from '@helpers/PhoneNumber';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { DeviceContact } from '@types';

const CONTACT_ID_PREFIX = 'contact_';
const CONTACT_FIELDS = [
  ExpoContacts.ContactField.FULL_NAME,
  ExpoContacts.ContactField.IMAGE,
  ExpoContacts.ContactField.PHONES,
  ExpoContacts.ContactField.EMAILS,
] as const;

type ExpoPhoneNumber = ExpoContacts.ExistingPhone & { countryCode?: string; isPrimary?: boolean };
type ExpoEmail = ExpoContacts.ExistingEmail;
export type DeviceExpoContact = {
  id: string;
  fullName?: string | null;
  image?: string | null;
  phones?: ExpoPhoneNumber[];
  emails?: ExpoEmail[];
};

function getContactId(contact: DeviceExpoContact) {
  return CONTACT_ID_PREFIX + contact.id;
}

export function isContactUser(contact: { userId: string }) {
  return contact.userId.startsWith(CONTACT_ID_PREFIX);
}

type ContactsPermissionsRequestParams = {
  force?: boolean;
};

export async function requestContactsPermissions({ force }: ContactsPermissionsRequestParams = {}) {
  const { status: currentStatus } = await ExpoContacts.getPermissionsAsync();
  if (currentStatus === PermissionStatus.UNDETERMINED || force) {
    const { status: newStatus } = await ExpoContacts.requestPermissionsAsync();
    logEvent('contacts_permissions', { status: newStatus });
    return newStatus === PermissionStatus.GRANTED;
  } else {
    logWarning('Not requesting contacts permissions (determined)');
    return currentStatus === PermissionStatus.GRANTED;
  }
}

export async function getContacts() {
  try {
    const { status } = await ExpoContacts.getPermissionsAsync();
    if (status === PermissionStatus.GRANTED) {
      const contacts = await ExpoContacts.Contact.getAllDetails(CONTACT_FIELDS);
      return parseContacts(contacts);
    } else {
      logWarning('Not getting contacts (permission not granted)');
    }
  } catch (error) {
    logError(error);
  }
  return [];
}

const isValidExpoContact = (contact: DeviceExpoContact) => {
  const hasId = contact.id;
  const hasName = contact.fullName;
  const hasPhoneNumber = contact.phones && contact.phones.length > 0;
  return hasId && hasName && hasPhoneNumber;
};

export function isValidContact(contact: Partial<DeviceContact> | null | undefined) {
  return (
    hasNonEmptyString(contact?.userId) &&
    hasNonEmptyString(contact?.contactId) &&
    hasNonEmptyString(contact?.displayName) &&
    hasNonEmptyString(contact?.phoneNumber) &&
    Array.isArray(contact?.phoneNumbers) &&
    contact?.phoneNumbers?.length > 0 &&
    Array.isArray(contact?.emails)
  );
}

export function parseContacts(expoContacts: DeviceExpoContact[]): DeviceContact[] {
  const contacts: DeviceContact[] = [];

  for (const expoContact of expoContacts) {
    try {
      if (isValidExpoContact(expoContact)) {
        const contact = parseContact(expoContact);
        if (isValidContact(contact)) {
          contacts.push(contact);
        }
      }
    } catch (error) {
      logError(error);
    }
  }

  return contacts;
}

function parseContact(contact: DeviceExpoContact): DeviceContact {
  return {
    userId: getCurrentUserId(),
    contactId: getContactId(contact),
    displayName: contact.fullName || '',
    photoUrl: contact.image || '',
    phoneNumber: contact.phones ? parsePrimaryPhoneNumber(contact.phones) : '',
    phoneNumbers: contact.phones ? parsePhoneNumbers(contact.phones) : [],
    emails: contact?.emails ? parseEmails(contact.emails) : [],
  };
}

function parseAndFormatPhoneNumber(number: ExpoPhoneNumber) {
  if (!number.number) {
    return undefined;
  }

  try {
    const parsedNumber = parsePhoneNumber(number.number, number.countryCode);
    return formatPhoneNumberToE164(parsedNumber);
  } catch (error) {
    logWarning(`Failed to parse phone number: ${error}`);
    logEvent('contact_phone_number_parse_failure', {
      error,
      number: number?.number,
      countryCode: number?.countryCode,
    });
  }
}

function parsePrimaryPhoneNumber(phoneNumbers: ExpoPhoneNumber[]) {
  const primary = phoneNumbers.find((number) => number.isPrimary) ?? phoneNumbers[0];
  return primary ? (parseAndFormatPhoneNumber(primary) ?? '') : '';
}

function parsePhoneNumbers(phoneNumbers: ExpoPhoneNumber[]) {
  return phoneNumbers
    .map((phoneNumber) => parseAndFormatPhoneNumber(phoneNumber))
    .filter((number): number is string => Boolean(number));
}

function parseEmails(emails: ExpoEmail[]) {
  return emails
    .filter((email): email is ExpoEmail & { address: string } => Boolean(email.address))
    .map((email) => email.address.trim().toLowerCase());
}

function hasNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}
